import { useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import { isAxiosError } from 'axios';

import { Section, SectionCard, SectionHeader } from '@/components/common';
import { datePickerLocaleText } from '@/utils/datePickerLocaleText';
import {
  useSkillsMatrixVersions,
  useParseSkillsMatrix,
  useApplySkillsMatrix,
} from '@/queries/useSkillsMatrix';
import { useAuthStore } from '@/stores/useAuthStore';
import { paths } from '@/routes/paths';
import type { SkillsMatrixVersion } from '@/api/types/skillsMatrix.types';
import { formatDateTime } from '@/utils/dateFormatter';

type VersionStatus = 'Active' | 'Scheduled' | 'Superseded';

function findActiveVersionId(versions: SkillsMatrixVersion[]): number | null {
  const now = new Date();
  const active = versions
    .filter((v) => new Date(v.effectiveFrom) <= now)
    .sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime())[0];
  return active?.id ?? null;
}

function computeStatus(version: SkillsMatrixVersion, activeId: number | null): VersionStatus {
  if (new Date(version.effectiveFrom) > new Date()) {
    return 'Scheduled';
  }
  if (version.id === activeId) {
    return 'Active';
  }
  return 'Superseded';
}

function getStatusChipColor(status: VersionStatus): 'success' | 'info' | 'default' {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Scheduled':
      return 'info';
    case 'Superseded':
      return 'default';
  }
}


const MAX_DISPLAYED_ERRORS = 5;

function getErrorMessages(error: unknown): { isDataValidation: boolean; messages: string[] } {
  if (!isAxiosError(error)) return { isDataValidation: false, messages: [] };
  const data = error.response?.data;
  if (!data) return { isDataValidation: false, messages: [] };

  // Structured response with errors array (data validation errors from controller)
  if (data.errors && Array.isArray(data.errors)) {
    return { isDataValidation: true, messages: data.errors };
  }

  // Single message (file format errors, generic errors)
  if (data.message) return { isDataValidation: false, messages: [data.message] };
  if (typeof data === 'string') return { isDataValidation: false, messages: [data] };
  return { isDataValidation: false, messages: [] };
}

export function SkillsMatrixPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState<Dayjs>(dayjs());
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const permissions = useAuthStore((state) => state.permissions);
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const canAccess = hasAnyRole(['Admin']);
  const { data: versions, isLoading } = useSkillsMatrixVersions();
  const parseMutation = useParseSkillsMatrix();
  const applyMutation = useApplySkillsMatrix();

  if (permissions && !canAccess) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFileName(file.name);
    parseMutation.reset();
    applyMutation.reset();
    parseMutation.mutate(file);

    // Reset the file input so re-selecting the same file triggers onChange
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleApply = async () => {
    if (!parseMutation.data || !selectedFileName) return;

    try {
      await applyMutation.mutateAsync({
        fileName: selectedFileName,
        skills: parseMutation.data,
        effectiveFrom: effectiveFrom.format('YYYY-MM-DD'),
      });

      // Clear step 2 on success
      parseMutation.reset();
      setSelectedFileName(null);
      setEffectiveFrom(dayjs());
      setSnackbarOpen(true);
    } catch {
      // applyMutation.error is rendered in JSX below
    }
  };

  const isApplying = applyMutation.isPending;
  const parseSucceeded = parseMutation.data && !parseMutation.error;
  const parseError = parseMutation.error
    ? getErrorMessages(parseMutation.error)
    : { isDataValidation: false, messages: [] };
  const parseErrorMessages = parseError.messages;
  const isFileFormatError = !parseError.isDataValidation && parseErrorMessages.length > 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} localeText={datePickerLocaleText}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Skills Matrix Management
        </Typography>

        <Section>
          <SectionHeader>UPLOAD NEW SKILLS MATRIX</SectionHeader>
          <SectionCard>
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              aria-label="Upload skills matrix file"
            />
            <Button
              variant="contained"
              color="secondary"
              startIcon={
                parseMutation.isPending ? <CircularProgress size={20} /> : <CloudUploadIcon />
              }
              onClick={handleUploadClick}
              disabled={parseMutation.isPending || isApplying}
            >
              {parseMutation.isPending ? 'Parsing...' : 'Upload File'}
            </Button>

            {parseMutation.error && isFileFormatError && (
              <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                The uploaded file could not be read. Please ensure you are uploading a valid Excel
                file (.xlsx) and try again.
              </Alert>
            )}

            {parseMutation.error && !isFileFormatError && parseErrorMessages.length > 0 && (
              <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  The file contains data errors that must be resolved:
                </Typography>
                {parseErrorMessages.slice(0, MAX_DISPLAYED_ERRORS).map((msg, i) => (
                  <Typography key={i} variant="body2">
                    {msg}
                  </Typography>
                ))}
                {parseErrorMessages.length > MAX_DISPLAYED_ERRORS && (
                  <Typography variant="body2">
                    ...and {parseErrorMessages.length - MAX_DISPLAYED_ERRORS} more errors
                  </Typography>
                )}
              </Alert>
            )}

            {parseSucceeded && (
              <>
                <Alert severity="success" sx={{ mt: 2 }}>
                  File parsed successfully. Found {parseMutation.data.length} skills in &quot;
                  {selectedFileName}&quot;.
                </Alert>

                <Typography variant="body2" fontWeight={600} sx={{ mt: 2 }}>
                  Effective From
                </Typography>
                <DatePicker
                  value={effectiveFrom}
                  onChange={(value) => {
                    if (value) setEffectiveFrom(value);
                  }}
                  disabled={isApplying}
                  format="DD MMM YYYY"
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { mt: 1, display: 'block', width: 160 },
                    },
                  }}
                />

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleApply}
                  disabled={isApplying}
                  startIcon={isApplying ? <CircularProgress size={20} /> : undefined}
                  sx={{ mt: 2 }}
                >
                  {isApplying ? 'Applying...' : 'Apply Skills Matrix'}
                </Button>

                {applyMutation.error && (
                  <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                    Failed to apply the skills matrix. Please try again.
                  </Alert>
                )}
              </>
            )}
          </SectionCard>
        </Section>

        <Section>
          <SectionHeader>VERSION HISTORY</SectionHeader>
          <SectionCard>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : !versions || versions.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 4, textAlign: 'center' }}
              >
                No skills matrix versions found. Upload one to get started.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Version</TableCell>
                      <TableCell>File Name</TableCell>
                      <TableCell>Uploaded By</TableCell>
                      <TableCell>Uploaded At</TableCell>
                      <TableCell>Effective From</TableCell>
                      <TableCell align="right" sx={{ pr: 3 }}>Skills</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const activeId = findActiveVersionId(versions);
                      return versions.map((version) => {
                        const status = computeStatus(version, activeId);
                        return (
                          <TableRow key={version.id}>
                            <TableCell>v{version.versionNumber}</TableCell>
                            <TableCell>{version.fileName}</TableCell>
                            <TableCell>{version.uploadedByName}</TableCell>
                            <TableCell>{formatDateTime(version.uploadedAt)}</TableCell>
                            <TableCell>{formatDateTime(version.effectiveFrom)}</TableCell>
                            <TableCell align="right" sx={{ pr: 3 }}>{version.skillCount}</TableCell>
                            <TableCell>
                              <Chip
                                label={status}
                                color={getStatusChipColor(status)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>
        </Section>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity="success">Skills matrix applied successfully.</Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}

export default SkillsMatrixPage;
